import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import GlassButton from './GlassButton';
import GlassProvider from './GlassProvider';
import AeroDotLoader from './AeroDotLoader';
import AeroIcon from './AeroIcon';
import { useRegion } from '../../hooks/useRegion';
import { usePricing } from '../../hooks/usePricing';
import './AeroPricingModal.css';

/**
 * What each plan includes, worded from docs/brand/PRICING-FACTS.md (the only
 * source for plan contents). Copy only: prices, trials and checkout come from
 * usePricing and the Stripe flow below and are not touched here.
 */
const AERO_FEATURES = {
  basic: [
    '5 test sessions',
    'Recordings, transcripts and summaries',
    'Your own aeroapp.ai address',
    'Password protection',
    'Custom favicon',
    'No Aero badge',
  ],
  solo: [
    '10 test sessions',
    'Everything in Basic',
    'Custom domain',
    'Site analytics',
  ],
  pro: [
    '20 test sessions',
    'Everything in Solo',
    'Priority support',
  ],
};

// Sessions are counted as stored at once, not a monthly allowance
// (PRICING-FACTS.md; SESSION_LIMITS in utils/userTestingLimits.js, mode 'total').
const SESSIONS_NOTE =
  'Session counts are how many you can keep at once. Delete old ones to free room. Free plan: 3 test sessions, and sites stay live for 7 days.';

// Largest whole-percent yearly saving across the plans for this region, so the
// toggle badge never overstates (prices are regional and differ per plan).
function maxYearlySavingPct(pricing) {
  let max = 0;
  ['basic', 'solo', 'pro'].forEach((key) => {
    const monthly = pricing?.[key]?.monthly?.amount;
    const yearly = pricing?.[key]?.yearly?.amount;
    if (monthly > 0 && yearly > 0) {
      const pct = Math.floor((1 - yearly / (monthly * 12)) * 100);
      if (pct > max) max = pct;
    }
  });
  return max;
}

const PLAN_ORDER = ['basic', 'solo', 'pro'];

const PLAN_DISPLAY = {
  basic: { name: 'Basic' },
  solo: { name: 'Solo', popular: true },
  pro: { name: 'Pro' },
};

/**
 * AeroPricingModal — Aero-branded upgrade modal with glass design system.
 *
 * Uses direct Cloud Function call for instant checkout (no Firestore round-trip).
 *
 * Props:
 *   show                    — boolean, whether to show the modal
 *   handleClose             — function, close callback
 *   trialConsume            — string|null, whether trial has been consumed
 *   planId                  — string|null, current plan price ID (for "Current Plan" label)
 *   prefetchedPricing       — object|null, pre-fetched pricing data (skips own fetch)
 *   prefetchedPricingLoading — boolean, whether pre-fetched pricing is still loading
 */
export default function AeroPricingModal({
  show,
  handleClose,
  trialConsume,
  planId,
  prefetchedPricing = null,
  prefetchedPricingLoading = false,
}) {
  const [isYearly, setIsYearly] = useState(false);
  const [checkingOutPlan, setCheckingOutPlan] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);

  // Use pre-fetched data if available, otherwise fetch our own
  const hasPrefetchedData = prefetchedPricing !== null || prefetchedPricingLoading;
  const { region: selfRegion } = useRegion(hasPrefetchedData ? 'US' : undefined);
  const { pricing: selfPricing, loading: selfPricingLoading } = usePricing(
    hasPrefetchedData ? 'US' : selfRegion
  );

  const pricing = hasPrefetchedData ? prefetchedPricing : selfPricing;
  const pricingLoading = hasPrefetchedData ? prefetchedPricingLoading : selfPricingLoading;

  // Reset checkout state when returning via browser back button (bfcache)
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        setCheckingOutPlan(null);
        setCheckoutError(null);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !checkingOutPlan) handleClose();
    };
    if (show) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [show, handleClose, checkingOutPlan]);

  if (!show) return null;

  const handleSelectPlan = async (planKey) => {
    const interval = isYearly ? 'yearly' : 'monthly';
    const priceInfo = pricing?.[planKey]?.[interval];
    const selectedPriceId = priceInfo?.priceId;
    if (!selectedPriceId) return;

    setCheckingOutPlan(planKey);
    setCheckoutError(null);

    // Determine trial days based on whether the user has consumed their trial
    let trialDays = 0;
    if (trialConsume !== 'true') {
      trialDays = isYearly ? 15 : 7;
    }

    // Set a 15-second timeout
    const timeoutId = setTimeout(() => {
      setCheckingOutPlan(null);
      setCheckoutError('Request timed out. Please try again.');
    }, 15000);

    try {
      const functions = getFunctions(undefined, 'us-central1');
      const createSession = httpsCallable(functions, 'createCheckoutSession');

      const { data } = await createSession({
        priceId: selectedPriceId,
        successUrl: window.location.origin + '/account?upgraded=true',
        cancelUrl: window.location.origin + '/account',
        trialDays,
      });

      clearTimeout(timeoutId);
      window.location.assign(data.url);
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Checkout failed:', error);
      setCheckingOutPlan(null);
      setCheckoutError(error.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="aero-pricing-overlay" onClick={checkingOutPlan ? undefined : handleClose}>
      <GlassProvider>
        <div className="aero-pricing-modal" onClick={(e) => e.stopPropagation()}>
          {/* Close button */}
          <button
            className="aero-pricing-close"
            onClick={handleClose}
            aria-label="Close"
            disabled={checkingOutPlan}
          >
            <AeroIcon name="x" />
          </button>

          {/* Header */}
          <h2 className="aero-pricing-title">Upgrade your Aero account</h2>

          {/* Error message */}
          {checkoutError && (
            <div className="aero-pricing-error">
              <p>{checkoutError}</p>
            </div>
          )}

          {/* Monthly / Yearly Toggle */}
          <div className="aero-pricing-toggle">
            <button
              className={`aero-pricing-toggle-btn ${!isYearly ? 'active' : ''}`}
              onClick={() => setIsYearly(false)}
              disabled={checkingOutPlan}
            >
              Monthly
            </button>
            <button
              className={`aero-pricing-toggle-btn ${isYearly ? 'active' : ''}`}
              onClick={() => setIsYearly(true)}
              disabled={checkingOutPlan}
            >
              Yearly
              {maxYearlySavingPct(pricing) > 0 && (
                <span className="aero-pricing-save-badge">
                  Save up to {maxYearlySavingPct(pricing)}%
                </span>
              )}
            </button>
          </div>

          {/* Plans Grid */}
          {pricingLoading ? (
            <div className="aero-pricing-loading">
              <AeroDotLoader size="md" />
              <p>Loading prices</p>
            </div>
          ) : (
            <div className="aero-pricing-grid">
              {PLAN_ORDER.map((key) => {
                const display = PLAN_DISPLAY[key];
                const features = AERO_FEATURES[key];
                const interval = isYearly ? 'yearly' : 'monthly';
                const priceInfo = pricing?.[key]?.[interval];
                const isCurrent = priceInfo?.priceId === planId;
                const isThisPlanLoading = checkingOutPlan === key;

                let buttonText = 'Get started';
                if (isCurrent) buttonText = 'Current plan';
                else if (trialConsume === 'true') buttonText = `Upgrade to ${display.name}`;
                else buttonText = isYearly ? 'Start 15-day free trial' : 'Start 7-day free trial';

                return (
                  <div
                    key={key}
                    className={`aero-pricing-card ${display.popular ? 'aero-pricing-card-popular' : ''} ${isCurrent ? 'aero-pricing-card-current' : ''}`}
                  >
                    <div className="aero-pricing-card-head">
                      <h3 className="aero-pricing-plan-name">{display.name}</h3>
                      {display.popular && !isCurrent && (
                        <span className="aero-pricing-badge">Most popular</span>
                      )}
                      {isCurrent && (
                        <span className="aero-pricing-badge aero-pricing-badge-current">Current plan</span>
                      )}
                    </div>

                    <div className="aero-pricing-price">
                      <span className="aero-pricing-amount">
                        {priceInfo?.displayAmount || '...'}
                      </span>
                      <span className="aero-pricing-interval">/month</span>
                    </div>

                    {isYearly && priceInfo?.billDesc && (
                      <p className="aero-pricing-bill-desc">{priceInfo.billDesc}</p>
                    )}

                    <GlassButton
                      fill={display.popular ? 'ink' : 'glass'}
                      size="md"
                      block
                      onClick={() => handleSelectPlan(key)}
                      disabled={isCurrent || checkingOutPlan !== null}
                    >
                      {isThisPlanLoading ? 'Opening checkout…' : buttonText}
                    </GlassButton>

                    <ul className="aero-pricing-features">
                      {features.map((f, i) => (
                        <li key={i} className="aero-pricing-feature">
                          <AeroIcon name="check" className="aero-pricing-check" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
          <p className="aero-pricing-note">{SESSIONS_NOTE}</p>
        </div>
      </GlassProvider>
    </div>
  );
}
